import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ===== Types =====

export interface LoadCreative {
  id: string;
  filename: string;
  creative_type: string;
  dimensions: string | null;
  aspect_ratio: string | null;
  meta_creative_id: string;
  thumbnail_url: string | null;
  group_id: string | null;
  group_type: string | null;
  upload_status: string | null;
  created_at: string | null;
}

export interface ResolvedRow {
  ad_format: string;
  ad_name: string;
  creative_feed: string | null;
  meta_creative_id_feed: string | null;
  creative_vertical: string | null;
  meta_creative_id_vertical: string | null;
  creative_horizontal: string | null;
  meta_creative_id_horizontal: string | null;
  thumbnail_url: string | null;
}

// ===== Aspect ratio helpers (3% tolerance, matching Meta specs) =====

function parseDimensions(dims: string | null): { width: number; height: number } | null {
  if (!dims) return null;
  const parts = dims.split("x").map(Number);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { width: parts[0], height: parts[1] };
}

export function isFeedRatio(creative: LoadCreative): boolean {
  if (creative.aspect_ratio === "square" || creative.aspect_ratio === "feed") return true;
  const d = parseDimensions(creative.dimensions);
  if (!d) return false;
  const ratio = d.width / d.height;
  return ratio >= 0.77 && ratio <= 1.97;
}

export function isVerticalRatio(creative: LoadCreative): boolean {
  if (creative.aspect_ratio === "vertical") return true;
  const d = parseDimensions(creative.dimensions);
  if (!d) return false;
  const ratio = d.width / d.height;
  return ratio >= 0.52 && ratio <= 0.60;
}

export function isHorizontalRatio(creative: LoadCreative): boolean {
  if (creative.aspect_ratio === "horizontal") return true;
  const d = parseDimensions(creative.dimensions);
  if (!d) return false;
  const ratio = d.width / d.height;
  return ratio >= 1.73 && ratio <= 1.83;
}

function classifySlot(creative: LoadCreative): "feed" | "vertical" | "horizontal" {
  if (isVerticalRatio(creative)) return "vertical";
  if (isHorizontalRatio(creative)) return "horizontal";
  return "feed"; // default to feed
}

function removeExtension(filename: string): string {
  return filename.replace(/\.[^/.]+$/, "");
}

// ===== Core resolution logic =====

export function resolveSelectedCreatives(
  selectedIds: Set<string>,
  allCreatives: LoadCreative[]
): Omit<ResolvedRow, "ad_name">[] {
  const processedGroups = new Set<string>();
  const rows: Omit<ResolvedRow, "ad_name">[] = [];

  for (const id of selectedIds) {
    const creative = allCreatives.find((c) => c.id === id);
    if (!creative) continue;

    if (creative.group_id) {
      if (processedGroups.has(creative.group_id)) continue;
      processedGroups.add(creative.group_id);

      // Find ALL group members (even unselected ones)
      const groupMembers = allCreatives.filter((c) => c.group_id === creative.group_id);

      let feedCreative: LoadCreative | null = null;
      let verticalCreative: LoadCreative | null = null;
      let horizontalCreative: LoadCreative | null = null;

      for (const member of groupMembers) {
        const slot = classifySlot(member);
        if (slot === "feed" && !feedCreative) feedCreative = member;
        else if (slot === "vertical" && !verticalCreative) verticalCreative = member;
        else if (slot === "horizontal" && !horizontalCreative) horizontalCreative = member;
      }

      const adFormat = "Single Image or Video";

      rows.push({
        ad_format: adFormat,
        creative_feed: feedCreative?.filename || null,
        meta_creative_id_feed: feedCreative?.meta_creative_id || null,
        creative_vertical: verticalCreative?.filename || null,
        meta_creative_id_vertical: verticalCreative?.meta_creative_id || null,
        creative_horizontal: horizontalCreative?.filename || null,
        meta_creative_id_horizontal: horizontalCreative?.meta_creative_id || null,
        thumbnail_url: feedCreative?.thumbnail_url || verticalCreative?.thumbnail_url || horizontalCreative?.thumbnail_url || null,
      });
    } else {
      // Ungrouped creative → 1 row
      const slot = classifySlot(creative);
      const adFormat = "Single Image or Video";

      rows.push({
        ad_format: adFormat,
        creative_feed: slot === "feed" ? creative.filename : null,
        meta_creative_id_feed: slot === "feed" ? creative.meta_creative_id : null,
        creative_vertical: slot === "vertical" ? creative.filename : null,
        meta_creative_id_vertical: slot === "vertical" ? creative.meta_creative_id : null,
        creative_horizontal: slot === "horizontal" ? creative.filename : null,
        meta_creative_id_horizontal: slot === "horizontal" ? creative.meta_creative_id : null,
        thumbnail_url: creative.thumbnail_url,
      });
    }
  }

  return rows;
}

export function calculateRowCount(selectedIds: Set<string>, allCreatives: LoadCreative[]): number {
  const processedGroups = new Set<string>();
  let count = 0;

  for (const id of selectedIds) {
    const creative = allCreatives.find((c) => c.id === id);
    if (!creative) continue;

    if (creative.group_id) {
      if (processedGroups.has(creative.group_id)) continue;
      processedGroups.add(creative.group_id);
    }
    count++;
  }

  return count;
}

// ===== Ad name generation =====

const FORMAT_CODES: Record<string, string> = {
  "Single Image or Video": "SGL",
  "Single Image": "SGL",
  "Single Video": "SGL",
  "Carousel": "CRSL",
  "Collection": "COLL",
  "Flexible": "FLX",
};

function resolveSeparator(convention: {
  separator?: string;
  custom_separator?: string;
}): string {
  return convention.separator === "custom"
    ? convention.custom_separator || "_"
    : convention.separator === "Hyphen"
      ? "-"
      : convention.separator === "Pipe"
        ? "|"
        : convention.separator === "Double Underscore"
          ? "__"
          : convention.separator === "Ampersand"
            ? "&"
            : "_";
}

export interface GenerateAdNameOptions {
  filename: string;
  adAccountId: string;
  userId: string;
  /** Ad format for {format} token */
  format?: string;
  /** Number of cards/assets for {card_count} token */
  cardCount?: number;
  /** Sequence number for {seq} token */
  seq?: number;
}

export async function generateAdName(
  filenameOrOpts: string | GenerateAdNameOptions,
  adAccountId?: string,
  userId?: string
): Promise<string> {
  // Support both legacy signature and new options object
  let opts: GenerateAdNameOptions;
  if (typeof filenameOrOpts === "string") {
    opts = { filename: filenameOrOpts, adAccountId: adAccountId!, userId: userId! };
  } else {
    opts = filenameOrOpts;
  }

  const baseName = removeExtension(opts.filename);
  const formatCode = opts.format ? (FORMAT_CODES[opts.format] || opts.format) : "";

  const { data: settings } = await supabase
    .from("ad_account_settings")
    .select("naming_convention")
    .eq("ad_account_id", opts.adAccountId)
    .eq("user_id", opts.userId)
    .maybeSingle();

  // Fallback naming when no convention is set
  if (!settings?.naming_convention) {
    return buildFallbackName(baseName, opts);
  }

  const convention = settings.naming_convention as {
    tokens?: string[];
    separator?: string;
    custom_separator?: string;
  };

  if (!convention.tokens || convention.tokens.length === 0) {
    return buildFallbackName(baseName, opts);
  }

  const sep = resolveSeparator(convention);
  const seqStr = opts.seq != null ? String(opts.seq).padStart(3, "0") : "";

  let name = convention.tokens
    .map((token: string) => {
      if (token === "filename" || token === "{creative_name}") return baseName;
      if (token === "date" || token === "{date}") {
        return new Date().toISOString().slice(0, 10);
      }
      if (token === "{format}") return formatCode;
      if (token === "{card_count}") {
        return opts.cardCount != null ? String(opts.cardCount) : "";
      }
      if (token === "{seq}") return seqStr;
      // Custom text tokens (not wrapped in braces) pass through
      if (!token.startsWith("{")) return token;
      // Unreplaced dynamic tokens → strip
      return "";
    })
    .filter(Boolean)
    .join(sep);

  // Clean double separators
  const escapedSep = sep.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  name = name.replace(new RegExp(`(${escapedSep}){2,}`, "g"), sep);

  return name || buildFallbackName(baseName, opts);
}

function buildFallbackName(baseName: string, opts: GenerateAdNameOptions): string {
  const formatCode = opts.format ? (FORMAT_CODES[opts.format] || opts.format) : "";

  if (formatCode === "CRSL" && opts.cardCount) {
    return `Carousel_${opts.cardCount}cards`;
  }
  if (formatCode === "COLL") {
    return `Collection_${baseName}`;
  }
  if (formatCode === "FLX" && opts.cardCount) {
    return `Flexible_${opts.cardCount}assets`;
  }
  // Single or unknown
  return baseName;
}

// ===== Hook =====

export function useLoadCreatives(adAccountId: string | undefined, userId: string | undefined) {
  return useQuery<LoadCreative[]>({
    queryKey: ["load-creatives", adAccountId, userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creative_metadata")
        .select(
          "id, filename, creative_type, dimensions, aspect_ratio, meta_creative_id, thumbnail_url, group_id, group_type, created_at"
        )
        .eq("meta_account_id", adAccountId!)
        .eq("user_id", userId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as LoadCreative[];
    },
    enabled: !!adAccountId && !!userId,
    staleTime: 30_000,
  });
}
